<template>
  <v2-modal
    :show="show"
    size="xl"
    :title="modalTitle"
    :use-top-layer="false"
    @close="onClose"
  >
    <!-- Step 1: filter + select shipments -->
    <div v-if="step === 1">
      <!-- Search by shipment ID -->
      <div class="v2-mb-3">
        <v2-input
          :value="queryParams.search"
          placeholder="Search by shipment ID..."
          @input="onSearch"
        />
      </div>

      <!-- Filters row -->
      <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4 bulk-export__filters">
        <div class="bulk-export__filter-item">
          <v2-multi-select
            :value="queryParams.entityNames"
            :option-groups="[{ title: isBrand ? 'Retailers' : 'Brands', options: entityNames }]"
            :placeholder="isBrand ? 'Retailers' : 'Brands'"
            :searchable="true"
            @input="onSelectNames"
            @search="loadEntityNames"
          />
        </div>
        <div class="bulk-export__filter-item">
          <v2-multi-select
            :value="queryParams.collectionNames"
            :option-groups="[{ title: 'Collections', options: collectionOptions }]"
            placeholder="Collections"
            :searchable="true"
            @input="onSelectCollections"
          />
        </div>
        <div class="bulk-export__filter-item">
          <v2-date-range-picker
            :key="`shipment-date-${filtersClearKey}`"
            :value="filterDate"
            placeholder="Shipment date"
            @change="onSelectRange"
          />
        </div>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="bulk-export__clear-filters"
          @click="onClearFilters"
        >
          Clear filters
        </button>
      </div>

      <div
        v-if="isLoading"
        class="v2-flex v2-justify-center v2-p-8"
      >
        <v2-spinner />
      </div>
      <template v-else>
        <!-- Selection toolbar -->
        <div class="v2-flex v2-justify-between v2-items-center v2-mb-2">
          <span class="v2-text-muted bulk-export__count">
            {{ selectedIds.length }} selected
          </span>
          <div class="v2-flex v2-gap-2">
            <v2-button
              v-if="shipments.length && !allCurrentMatchesSelected"
              variant="ghost"
              size="sm"
              @click="selectAllMatching"
            >
              Select all {{ totalShipments }} matching
            </v2-button>
            <v2-button
              v-if="selectedIds.length"
              variant="ghost"
              size="sm"
              @click="clearSelection"
            >
              Clear selection
            </v2-button>
          </div>
        </div>

        <!-- Shipments table -->
        <v2-card :no-padding="true">
          <div class="bulk-export__table-scroll">
            <v2-table
              :columns="tableColumns"
              :items="shipments"
            >
              <template #empty>
                <p class="v2-text-muted v2-text-center v2-p-4">
                  No shipments match the selected filters.
                </p>
              </template>

              <template #cell(selected)="{ item }">
                <v2-checkbox
                  :value="isSelected(item._id)"
                  @input="toggleRow(item)"
                />
              </template>

              <template #cell(name)="{ item }">
                <span class="v2-text-nowrap">
                  {{ isBrand ? item.retailerName : item.brandName }}
                </span>
              </template>

              <template #cell(status)="{ item }">
                <v2-badge :variant="statusVariant(item.status)">
                  {{ item.status }}
                </v2-badge>
              </template>

              <template #cell(createdAt)="{ item }">
                <span class="v2-text-nowrap">
                  {{ getDateFromTimestamp(item.createdAt) }}
                </span>
              </template>
            </v2-table>
          </div>
        </v2-card>

        <v2-pagination
          v-if="hasPagination"
          class="v2-mt-4"
          :current-page="queryParams.page"
          :total-items="totalShipments"
          :per-page="queryParams.limit"
          @page-change="onPageChange"
        />
      </template>
    </div>

    <!-- Step 2: choose export type(s) -->
    <div v-else>
      <p class="bulk-export__step-title v2-mb-1">
        Choose what to export
      </p>
      <p class="v2-text-muted v2-mb-4">
        {{ selectedIds.length }} shipment(s) selected. Each chosen type is added
        as a separate file inside a single ZIP.
      </p>
      <div class="bulk-export__types">
        <label
          v-for="option in exportTypeOptions"
          :key="option.value"
          class="bulk-export__type-row"
        >
          <v2-checkbox
            :value="selectedExportTypes.includes(option.value)"
            @input="toggleExportType(option.value)"
          />
          <span>{{ option.text }}</span>
        </label>
      </div>
    </div>

    <!-- Invoice availability confirmation -->
    <v2-modal
      :show="showInvoiceConfirm"
      size="md"
      title="Some shipments have no invoice"
      :use-top-layer="true"
      @close="cancelInvoiceConfirm"
    >
      <p class="v2-mb-2">
        {{ missingInvoiceCount }} of {{ selectedIds.length }} selected shipment(s)
        have no uploaded invoice and will be skipped:
      </p>
      <ul
        v-if="missingInvoiceShipments.length"
        class="bulk-export__missing-list v2-mb-3"
      >
        <li
          v-for="shipment in missingInvoiceShipments"
          :key="shipment.id"
        >
          {{ shipment.shipmentSeqId || shipment.id }}
        </li>
      </ul>
      <p class="v2-text-muted">
        Do you want to continue with the download?
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="submitting"
          @click="cancelInvoiceConfirm"
        >
          Cancel
        </v2-button>
        <v2-button
          :loading="submitting"
          :disabled="submitting"
          @click="proceedAfterInvoiceConfirm"
        >
          Continue
        </v2-button>
      </template>
    </v2-modal>

    <template #footer>
      <v2-button
        variant="outline"
        :disabled="submitting"
        @click="onBack"
      >
        {{ step === 1 ? 'Cancel' : 'Back' }}
      </v2-button>
      <v2-button
        v-if="step === 1"
        :disabled="!selectedIds.length"
        @click="step = 2"
      >
        Next ({{ selectedIds.length }})
      </v2-button>
      <v2-button
        v-else
        :loading="submitting"
        :disabled="!selectedExportTypes.length || submitting"
        @click="onConfirm"
      >
        Export
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2MultiSelect from '@/components/v2/V2MultiSelect.vue'
import V2DateRangePicker from '@/components/v2/V2DateRangePicker.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import moment from 'moment'
import { getDateFromTimestamp } from '@/common-utils'
import { getSearchByEndpoint } from '@/views/apps/order/order-utils'
import { apiToastError, apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import {
  GET_SHIPMENT_LIST,
  GET_SHIPMENT_LIST_ENTITY_NAMES,
  BULK_EXPORT_SHIPMENTS,
  GET_SHIPMENT_INVOICE_AVAILABILITY,
} from '@/store/modules/shipment.module'
import { GET_COLLECTION_LIST } from '@/store/modules/collection.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'

const EXPORT_TYPE_OPTIONS = [
  { text: 'SKU-wise', value: 'sku' },
  { text: 'Style-wise', value: 'style' },
  { text: 'Shipment Details (PDF)', value: 'shipmentDetails' },
  { text: 'Invoice', value: 'invoice' },
]

// Upper bound for "Select all matching" so a huge filter result can't pull
// thousands of records into memory / freeze the UI in one fetch.
const MAX_SELECT_ALL = 1000

export default {
  name: 'ShipmentBulkExportModal',
  components: {
    V2Modal,
    V2Button,
    V2Input,
    V2Table,
    V2Card,
    V2MultiSelect,
    V2DateRangePicker,
    V2Pagination,
    V2Checkbox,
    V2Badge,
    V2Spinner,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      submitting: false,
      showInvoiceConfirm: false,
      missingInvoiceCount: 0,
      missingInvoiceShipments: [],
      shipments: [],
      totalShipments: 0,
      entityNames: [],
      collections: [],
      // id -> shipment object. Storing the object (not just the id) lets us
      // prune the selection by retailer/collection when a filter is deselected.
      selectedShipments: {},
      selectedExportTypes: [],
      filterDate: null,
      filtersClearKey: 0,
      searchTimer: null,
      queryParams: {
        page: 1,
        limit: 10,
        search: '',
        entityNames: [],
        collectionNames: [],
        minShipmentDate: '',
        maxShipmentDate: '',
      },
      exportTypeOptions: EXPORT_TYPE_OPTIONS,
    }
  },
  computed: {
    isBrand() {
      return this.$store.getters.isBrand
    },
    selectedIds() {
      return Object.keys(this.selectedShipments)
    },
    modalTitle() {
      return this.step === 1 ? 'Select shipments to export' : 'Choose export type'
    },
    hasPagination() {
      return this.totalShipments > this.queryParams.limit
    },
    allCurrentMatchesSelected() {
      // Hide "Add all matching" only when the entire filtered result fits on the
      // current page AND every row is selected. With more pages, keep it visible
      // since later pages may hold unselected matching shipments. (We can't check
      // other pages without fetching them, and comparing the global selection
      // count to totalShipments would wrongly hide it across multi-filter
      // selections, so this stays conservative — it never hides while there is
      // anything left to add.)
      return (
        this.shipments.length > 0
        && this.totalShipments <= this.shipments.length
        && this.shipments.every(shipment => this.selectedIds.includes(shipment._id))
      )
    },
    hasActiveFilters() {
      return !!(
        this.queryParams.search
        || this.queryParams.entityNames.length
        || this.queryParams.collectionNames.length
        || this.queryParams.minShipmentDate
      )
    },
    collectionOptions() {
      return this.collections.map(collection => collection.name)
    },
    tableColumns() {
      return [
        { key: 'selected', label: '', width: '48px' },
        { key: 'shipmentSeqId', label: 'Shipment ID' },
        { key: 'name', label: this.isBrand ? 'Retailer' : 'Brand' },
        { key: 'status', label: 'Status' },
        { key: 'createdAt', label: 'Date' },
      ]
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.resetState()
        this.loadShipments()
        this.loadEntityNames()
        this.loadCollections()
      }
    },
  },
  beforeDestroy() {
    // Cancel any pending debounced search so it can't fire after unmount.
    clearTimeout(this.searchTimer)
  },
  methods: {
    getDateFromTimestamp,
    resetState() {
      this.step = 1
      this.submitting = false
      this.showInvoiceConfirm = false
      this.missingInvoiceCount = 0
      this.missingInvoiceShipments = []
      this.selectedShipments = {}
      this.selectedExportTypes = []
      this.filterDate = null
      this.entityNames = []
      this.filtersClearKey += 1
      this.queryParams = {
        page: 1,
        limit: 10,
        search: '',
        entityNames: [],
        collectionNames: [],
        minShipmentDate: '',
        maxShipmentDate: '',
      }
    },
    buildParams() {
      // Drop empty filter values (blank strings, null, empty arrays) so we never
      // send blank query params.
      const params = { ...this.queryParams, page: this.queryParams.page - 1 }
      Object.keys(params).forEach(key => {
        const value = params[key]
        const isEmpty = value === '' || value == null
          || (Array.isArray(value) && value.length === 0)
        if (isEmpty) delete params[key]
      })
      return params
    },
    // Safely read the shipments + count out of a list response, defaulting to
    // empty when the payload is partial/malformed. Shared by loadShipments and
    // selectAllMatching so the optional-chaining lives in one place.
    extractShipmentList(res) {
      const data = res?.data?.data || {}
      return { shipments: data.shipments || [], count: data.count || 0 }
    },
    statusVariant(status) {
      return String(status).toLowerCase().includes('confirm') ? 'success' : 'default'
    },
    isSelected(id) {
      return !!this.selectedShipments[id]
    },
    clearSelection() {
      this.selectedShipments = {}
    },
    toggleRow(shipment) {
      const next = { ...this.selectedShipments }
      if (next[shipment._id]) {
        delete next[shipment._id]
      } else {
        next[shipment._id] = shipment
      }
      this.selectedShipments = next
    },
    toggleExportType(value) {
      if (this.selectedExportTypes.includes(value)) {
        this.selectedExportTypes = this.selectedExportTypes.filter(type => type !== value)
      } else {
        this.selectedExportTypes = [...this.selectedExportTypes, value]
      }
    },
    selectAllMatching() {
      if (!this.totalShipments) return
      this.isLoading = true
      const limit = Math.min(this.totalShipments, MAX_SELECT_ALL)
      if (this.totalShipments > MAX_SELECT_ALL) {
        apiToastWarning(
          `Only the first ${MAX_SELECT_ALL} matching shipments can be selected at once.`,
        )
      }
      const params = { ...this.buildParams(), page: 0, limit }
      this.$store
        .dispatch(GET_SHIPMENT_LIST, params)
        .then(res => {
          // Union with the existing selection so selections accumulate across
          // successive filters instead of being replaced each time.
          const next = { ...this.selectedShipments }
          this.extractShipmentList(res).shipments.forEach(shipment => {
            next[shipment._id] = shipment
          })
          this.selectedShipments = next
        })
        .catch(err => {
          apiToastError(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadCollections() {
      this.$store
        .dispatch(GET_COLLECTION_LIST)
        .then(res => {
          this.collections = res?.data?.data?.collections || []
        })
        .catch(() => {
          this.collections = []
        })
    },
    onSelectCollections(names = []) {
      const removed = this.queryParams.collectionNames.filter(name => !names.includes(name))
      this.queryParams.collectionNames = names
      this.pruneSelectionByRemovedValues(removed, shipment => shipment.collectionName)
      this.queryParams.page = 1
      this.loadShipments()
    },
    loadEntityNames(searchText = '') {
      this.$store
        .dispatch(GET_SHIPMENT_LIST_ENTITY_NAMES, {
          role: getSearchByEndpoint(this.isBrand),
          params: { search: searchText },
        })
        .then(res => {
          this.entityNames = res?.data?.data?.values || []
        })
        .catch(() => {
          this.entityNames = []
        })
    },
    onSearch(text = '') {
      this.queryParams.search = text
      this.queryParams.page = 1
      // Debounce so we don't fire a request on every keystroke.
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.loadShipments()
      }, 400)
    },
    onSelectNames(names = []) {
      const removed = this.queryParams.entityNames.filter(name => !names.includes(name))
      this.queryParams.entityNames = names
      // The entity filter matches the counterparty name: retailers for a brand,
      // brands for a retailer.
      this.pruneSelectionByRemovedValues(
        removed,
        shipment => (this.isBrand ? shipment.retailerName : shipment.brandName),
      )
      this.queryParams.page = 1
      this.loadShipments()
    },
    // Drop selected shipments whose filter value was just deselected, so removing
    // a retailer/collection from the filter unselects that group's shipments
    // (while selections from the still-active filters persist).
    pruneSelectionByRemovedValues(removedValues, getShipmentValue) {
      if (!removedValues.length) return
      const next = { ...this.selectedShipments }
      Object.keys(next).forEach(id => {
        if (removedValues.includes(getShipmentValue(next[id]))) {
          delete next[id]
        }
      })
      this.selectedShipments = next
    },
    onSelectRange(dates = []) {
      // Mirror the order/shipment list pages: NEVER reassign `filterDate` here.
      // `filterDate` is the controlled `:value`; writing it back from @change
      // re-feeds flatpickr and re-fires @change in an infinite loop (the page
      // hang). The picker keeps its own state; we only update the query.
      if (dates.length === 2) {
        const [start, end] = dates
        // flatpickr emits both ends at local 00:00; send start-of-day / end-of-day
        // so a same-day ("Today") range includes the whole selected day instead of
        // collapsing to the previous day at the backend's $lte boundary.
        this.queryParams.minShipmentDate = moment(start).startOf('day').toISOString()
        this.queryParams.maxShipmentDate = moment(end).endOf('day').toISOString()
      } else if (!dates.length) {
        this.queryParams.minShipmentDate = ''
        this.queryParams.maxShipmentDate = ''
      } else {
        return
      }
      this.queryParams.page = 1
      this.loadShipments()
    },
    onClearFilters() {
      this.filterDate = null
      this.filtersClearKey += 1
      this.queryParams.search = ''
      this.queryParams.entityNames = []
      this.queryParams.collectionNames = []
      this.queryParams.minShipmentDate = ''
      this.queryParams.maxShipmentDate = ''
      this.queryParams.page = 1
      this.loadShipments()
    },
    onPageChange(page) {
      this.queryParams.page = page
      this.loadShipments()
    },
    loadShipments() {
      this.isLoading = true
      const params = this.buildParams()
      return this.$store
        .dispatch(GET_SHIPMENT_LIST, params)
        .then(res => {
          const { shipments, count } = this.extractShipmentList(res)
          this.shipments = shipments
          this.totalShipments = count
        })
        .catch(err => {
          apiToastError(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onBack() {
      if (this.step === 1) {
        this.onClose()
      } else {
        this.step = 1
      }
    },
    onClose() {
      this.$emit('close')
    },
    async onConfirm() {
      // When invoices are requested, check availability first and pause for
      // confirmation if some shipments have no uploaded invoice.
      if (this.selectedExportTypes.includes('invoice')) {
        const paused = await this.checkInvoiceAvailability()
        if (paused) return
      }
      this.triggerExport()
    },
    // Returns true when the export should pause — either the confirmation dialog
    // is now shown (some invoices missing) or the availability check failed.
    async checkInvoiceAvailability() {
      this.submitting = true
      try {
        const res = await this.$store.dispatch(
          GET_SHIPMENT_INVOICE_AVAILABILITY,
          { shipmentIds: this.selectedIds },
        )
        const data = res?.data?.data || {}
        if (!data.missing) return false
        this.missingInvoiceCount = data.missing
        this.missingInvoiceShipments = data.missingShipments || []
        this.showInvoiceConfirm = true
        this.submitting = false
        return true
      } catch (err) {
        apiToastError(err)
        this.submitting = false
        return true
      }
    },
    cancelInvoiceConfirm() {
      this.showInvoiceConfirm = false
      this.submitting = false
    },
    proceedAfterInvoiceConfirm() {
      this.showInvoiceConfirm = false
      this.triggerExport()
    },
    triggerExport() {
      this.submitting = true
      this.$store
        .dispatch(BULK_EXPORT_SHIPMENTS, {
          shipmentIds: this.selectedIds,
          exportTypes: this.selectedExportTypes,
        })
        .then(res => {
          apiToastSuccess(res.data.message)
          this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
          this.$emit('created')
          this.onClose()
        })
        .catch(err => {
          apiToastError(err)
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.bulk-export {
  &__filters {
    flex-wrap: wrap;
  }

  &__filter-item {
    flex: 1;
    min-width: 180px;
  }

  &__clear-filters {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-foreground);
    }
  }

  &__count {
    font-size: 0.8125rem;
  }

  &__step-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--v2-foreground);
  }

  // Bound height so a large selection list stays scrollable inside the modal.
  &__table-scroll {
    max-height: 360px;
    overflow-y: auto;
  }

  // Bounded, scrollable list of shipments missing an invoice in the confirm dialog.
  &__missing-list {
    max-height: 180px;
    overflow-y: auto;
    margin: 0;
    padding-left: var(--v2-space-5);
    font-size: 0.8125rem;
    color: var(--v2-foreground);

    li {
      margin-bottom: var(--v2-space-1);
    }
  }

  &__types {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
  }

  &__type-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-3);
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    font-size: 0.875rem;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: var(--v2-glass-stroke-strong);
    }
  }
}
</style>
